import React, { useMemo } from "react";
import { Select, Tag, Space, Typography } from "antd";
import {
  BankOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  CreditCardOutlined,
  UserOutlined
} from "@ant-design/icons";

const { Text } = Typography;

export type TreasuryType = "CASH" | "BANK" | "WALLET" | "CREDIT_CARD" | "PARTNER";

export interface TreasuryAccountItem {
  id: number;
  name: string;
  type: TreasuryType;
  bank_name?: string | null;
  account_number?: string | null;
  is_default_cash?: boolean;
  is_default_bank?: boolean;
  allow_inflows?: boolean;
  allow_outflows?: boolean;
  contact_id?: number | null;
  contact?: {
    id: number;
    name: string;
  } | null;
  current_balance?: number;
}

export interface TreasuryAccountSelectProps {
  accounts: TreasuryAccountItem[];
  value?: number | null;
  onChange?: (value: number | null, account?: TreasuryAccountItem | null) => void;
  direction?: "INFLOW" | "OUTFLOW"; // INFLOW = cobro/recaudo, OUTFLOW = pago/egreso
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

const getAccountIcon = (type: TreasuryType) => {
  switch (type) {
    case "BANK":
      return <BankOutlined style={{ color: "#1890ff" }} />;
    case "WALLET":
      return <WalletOutlined style={{ color: "#722ed1" }} />;
    case "CREDIT_CARD":
      return <CreditCardOutlined style={{ color: "#fa8c16" }} />;
    case "PARTNER":
      return <UserOutlined style={{ color: "#13c2c2" }} />;
    case "CASH":
    default:
      return <DollarCircleOutlined style={{ color: "#52c41a" }} />;
  }
};

export const TreasuryAccountSelect: React.FC<TreasuryAccountSelectProps> = ({
  accounts = [],
  value,
  onChange,
  direction = "OUTFLOW",
  placeholder = "Seleccionar cuenta de tesorería...",
  disabled = false,
  allowClear = true,
  style = { width: "100%" },
  className
}) => {
  const filteredAccounts = useMemo(() => {
    return accounts.filter((acc) => {
      if (direction === "INFLOW" && acc.allow_inflows === false) return false;
      if (direction === "OUTFLOW" && acc.allow_outflows === false) return false;
      return true;
    });
  }, [accounts, direction]);

  const companyAccounts = useMemo(() => {
    return filteredAccounts.filter((a) => a.type !== "PARTNER");
  }, [filteredAccounts]);

  const partnerAccounts = useMemo(() => {
    return filteredAccounts.filter((a) => a.type === "PARTNER");
  }, [filteredAccounts]);

  const handleChange = (selectedId: number | undefined) => {
    if (!onChange) return;
    if (selectedId === undefined || selectedId === null) {
      onChange(null, null);
    } else {
      const found = accounts.find((a) => a.id === selectedId) || null;
      onChange(selectedId, found);
    }
  };

  return (
    <Select
      value={value ?? undefined}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      style={style}
      className={className}
      showSearch
      optionFilterProp="label"
    >
      {companyAccounts.length > 0 && (
        <Select.OptGroup label="Cuentas de la Empresa">
          {companyAccounts.map((acc) => {
            const subtitle = acc.bank_name
              ? `${acc.bank_name}${acc.account_number ? ` *${acc.account_number.slice(-4)}` : ""}`
              : acc.type === "CASH"
              ? "Efectivo"
              : undefined;

            return (
              <Select.Option key={acc.id} value={acc.id} label={`${acc.name} ${subtitle || ""}`}>
                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                  <Space>
                    {getAccountIcon(acc.type)}
                    <span>{acc.name}</span>
                    {acc.is_default_bank && (
                      <Tag color="blue" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                        Banco Principal
                      </Tag>
                    )}
                    {acc.is_default_cash && (
                      <Tag color="green" style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                        Caja Principal
                      </Tag>
                    )}
                  </Space>
                  {subtitle && <Text type="secondary" style={{ fontSize: 12 }}>{subtitle}</Text>}
                </Space>
              </Select.Option>
            );
          })}
        </Select.OptGroup>
      )}

      {partnerAccounts.length > 0 && (
        <Select.OptGroup label="Cuentas de Socios / Accionistas">
          {partnerAccounts.map((acc) => {
            const partnerName = acc.contact?.name || acc.name;
            const tagMsg = direction === "OUTFLOW"
              ? "Socio paga con su dinero (2355)"
              : "Socio recibe en su cuenta (1325)";

            return (
              <Select.Option key={acc.id} value={acc.id} label={`${acc.name} ${partnerName}`}>
                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                  <Space>
                    {getAccountIcon(acc.type)}
                    <span>{acc.name}</span>
                    <Tag color={direction === "OUTFLOW" ? "purple" : "orange"} style={{ fontSize: 10, padding: "0 4px", margin: 0 }}>
                      {tagMsg}
                    </Tag>
                  </Space>
                  {acc.contact && <Text type="secondary" style={{ fontSize: 12 }}>{acc.contact.name}</Text>}
                </Space>
              </Select.Option>
            );
          })}
        </Select.OptGroup>
      )}
    </Select>
  );
};

export default TreasuryAccountSelect;

